import React from 'react';
import { PrimaryButton, SecondaryButton } from '..';

import css from './PendingRequestCard.module.css';

const formatDate = d => {
  if (!d) return '';
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date)) return '';
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Money is formatted locally rather than through formatMoney(intl, …) so this
// card stays a plain presentational component. Currency code is always shown —
// "$950.59" alone is ambiguous on a marketplace running CAD and USD.
const formatAmount = money => {
  if (!money || typeof money.amount !== 'number') return null;
  const currency = (money.currency || '').toUpperCase();
  return `$${(money.amount / 100).toFixed(2)}${currency ? ` ${currency}` : ''}`;
};

const PendingRequestCard = props => {
  const {
    requestType,
    requestedBy,
    isViewerRequester,
    reason,
    proposedStart,
    proposedEnd,
    refundAmount,
    onAccept,
    onDecline,
    acceptInProgress,
    declineInProgress,
    error,
  } = props;

  const isCancellation = requestType === 'cancellation';
  const isDateChange = requestType === 'date-change';

  const heading = isCancellation
    ? isViewerRequester
      ? 'You requested to cancel this booking'
      : `${requestedBy === 'customer' ? 'The renter' : 'The host'} requested to cancel this booking`
    : isViewerRequester
    ? 'You requested new dates'
    : `${requestedBy === 'customer' ? 'The renter' : 'The host'} requested new dates`;

  const waitingMessage = isViewerRequester
    ? `Waiting on the ${requestedBy === 'customer' ? 'host' : 'renter'}.`
    : 'They are waiting on your response.';

  // The responder's money is held until they act, and "They are waiting on your
  // response" never said so — renters read it as informational and left the
  // booking parked, which freezes the calendar and their refund with no way for
  // support to intervene. Spell out the consequence of each button.
  const formattedRefund = formatAmount(refundAmount);
  const viewerIsRenter = !isViewerRequester && requestedBy === 'provider';
  const stakesMessage = !isCancellation
    ? null
    : isViewerRequester
    ? null
    : viewerIsRenter
    ? `Accepting cancels the booking and refunds you${
        formattedRefund ? ` ${formattedRefund}` : ' in full'
      }. Your refund is not issued until you accept. Declining keeps the booking as it is.`
    : `Accepting cancels the booking and refunds the renter${
        formattedRefund ? ` ${formattedRefund}` : ' in full'
      } — you won't be paid for this booking. Declining keeps the booking as it is.`;

  const inProgress = acceptInProgress || declineInProgress;

  return (
    <div className={css.card}>
      <div className={css.header}>
        <span className={css.badge}>{isCancellation ? 'Cancellation request' : 'Date change'}</span>
        <h3 className={css.title}>{heading}</h3>
      </div>

      {isDateChange && (proposedStart || proposedEnd) ? (
        <div className={css.proposedDates}>
          <div className={css.dateLine}>
            <span className={css.dateLabel}>Proposed start</span>
            <span className={css.dateValue}>{formatDate(proposedStart)}</span>
          </div>
          <div className={css.dateLine}>
            <span className={css.dateLabel}>Proposed end</span>
            <span className={css.dateValue}>{formatDate(proposedEnd)}</span>
          </div>
        </div>
      ) : null}

      {reason ? (
        <div className={css.reasonBlock}>
          <span className={css.reasonLabel}>Reason</span>
          <p className={css.reason}>{reason}</p>
        </div>
      ) : null}

      <p className={css.waiting}>{waitingMessage}</p>

      {stakesMessage ? <p className={css.stakes}>{stakesMessage}</p> : null}

      {error ? <p className={css.error}>{error}</p> : null}

      {!isViewerRequester ? (
        <div className={css.actions}>
          <SecondaryButton
            type="button"
            onClick={onDecline}
            inProgress={declineInProgress}
            disabled={inProgress}
          >
            Decline
          </SecondaryButton>
          <PrimaryButton
            type="button"
            onClick={onAccept}
            inProgress={acceptInProgress}
            disabled={inProgress}
          >
            {isCancellation ? 'Accept & cancel booking' : 'Accept new dates'}
          </PrimaryButton>
        </div>
      ) : null}
    </div>
  );
};

export default PendingRequestCard;
